import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { PLAYER_CONFIG } from '../../../config/player.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DifficultyManager } from '../../../difficulty/DifficultyManager';
import { deriveJumpPhysics } from '../../../physics/JumpPhysics';
import { jumpHeightAt } from '../../../physics/JumpReach';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLine, entryConnector, laneCenterX, moduleSides, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type CollectibleSpawnPoint, type SegmentDefinition } from '../SegmentDefinition';
import { RAMP, SEG_RAMP_40M } from './SEG_RAMP_40M';

/**
 * SEG_GOLDEN_RAMP_60M – "Goldene Flasche" (user 2026-10-02: "eine große goldene Flasche, die auch ein
 * bisschen glänzt … nach einer Rampe … am Ende der Rampe mit einem Doppelsprung erreicht man diese
 * Flasche. Sonst nicht." – "eine Flasche machen wir alle fünfhundert Meter nachher wenn es schneller wird ab
 * zweitausend Meter dann immer nur in tausender Schritten"). Scheduled set piece: once at each mark of
 * GOLDEN_RAMP.placement – 500, 1000, 1500, 2000 m, then 3000, 4000, 5000 m … endlessly.
 *
 *   z 0–32    the ramp of SEG_RAMP_40M – same terrain, same Blender overlay (ENV_Ramp_A): ramp z 4–14 in
 *             the centre lane up to the platform z 14–32 at 1.8 m
 *   z 41      the golden bottle (COL_GOLDEN_BOTTLE_A, 2.4× ≈ 1.8 m) hangs over the centre lane, its bottom
 *             6.2 m above the road – 9 m past the platform edge, where a double jump from the edge peaks
 *   z 32–60   free run-out: the drop from the platform is a normal landing (no pit), nothing to dodge –
 *             a double jump from the edge lands by z ≈ 58 even at top speed (18 m/s)
 *   lanes 0/2 plain ground without obstacles – the set piece's calm moment
 *
 * Reach (tests/unit/goldenRamp.test.ts, physics/JumpReach.ts): the feet must be 3.92 m above the road at
 * the bottle. A single jump from the platform peaks at 1.8 + 1.5 = 3.3 m, a double jump from the road at
 * 3.0 m – both miss; a double jump from the platform end gets up to 4.8 m. From the first mark (500 m,
 * 9.9 m/s) to top speed (18 m/s) it leaves ≥ 0.36 s for the second jump and ≥ 0.35 s of take-off points,
 * and the plain move – jump at the edge, second jump at the top – hits at every speed. The bottle used to
 * hang at z 42 / 6.3 m (tuned for 14 m/s): with the first mark at 500 m that left only 0.22 s of take-off
 * points at 9.9 m/s, and a second jump at the top fell short below ≈ 9 m/s. One fixed spot for all speeds
 * keeps the set piece the same every time – no speed-dependent variants needed.
 * The bottle line runs up the ramp, along the platform and on along that double-jump arc.
 */
export const GOLDEN_RAMP = {
  length: 60,
  bottle: {
    collectibleId: 'COL_GOLDEN_BOTTLE_A',
    spawnPointId: 'SOCKET_COL_GOLDEN',
    lane: RAMP.lane,
    /** 9 m past the platform edge (RAMP.platform.zTo): fair from 9 to 18 m/s (the schedule starts at 9.9 m/s). */
    z: 41,
    /** Bottom of the bottle above the road (m) – pickup bottom 5.72 m (scaled pickup volume, CollectibleCatalog). */
    height: 6.2,
    /** Visual and pickup size factor: a big bottle, about as tall as the runner. */
    scale: 2.4,
  },
  /** Bottle line along the ideal path: take-off this far before the edge (runner centre), double jump at the apex. */
  guideTakeOffBeforeEdge: 0.5,
  /** The guide arc after the edge ends before the golden bottle's pickup volume. */
  guideArc: { from: 33, to: 39 },
  placement: {
    /**
     * Marks (run distance, m) – once at each, in order. User: "alle fünfhundert Meter … ab zweitausend Meter
     * dann immer nur in tausender Schritten": every 500 m up to 2000 m, then every 1000 m, endlessly.
     */
    stretches: [
      { from: 500, every: 500, until: 2000 },
      { from: 3000, every: 1000 },
    ],
    /**
     * Meters after a mark in which the ramp starts – it comes right after the mark (40 seeds × 8 km: median
     * 40 m, 90 % within 60 m, at most 160 m); past it the module stops waiting for the open straight (never skipped).
     */
    window: 200,
    /**
     * From this far past the mark the ramp takes the first spot that fits; before, it takes the share of the
     * calm modules (moving hazards keep their frequency – LevelGenerator.chooseSetPiece).
     */
    forceAfter: 20,
    /** Open straight right before the module (m): with the module's own 41 m ≈ 80 m of straight before the bottle. */
    sightLine: 40,
    /** The user's marks are exact: where the dozer schedule (≈ every 600 m) meets them, the dozer gives way. */
    priority: 1,
  },
} as const;

const SPACING = 2;
const PHYSICS = deriveJumpPhysics(PLAYER_CONFIG);
const { stretches, window, forceAfter, sightLine, priority } = GOLDEN_RAMP.placement;
const FIRST_MARK = stretches[0].from;
/** The guide arc is laid out for the middle of the speed range the ramp is met at (first mark … top speed, m/s). */
const GUIDE_SPEED = (new DifficultyManager(DIFFICULTY_CONFIG).speedAt(FIRST_MARK) + DIFFICULTY_CONFIG.speed.max) / 2;

/** Bottle height along the guide line: up the ramp, along the platform, then the double-jump arc from its end. */
function guideHeight(z: number): number {
  const { ramp, platform, height } = RAMP;
  if (z <= ramp.zFrom) return COLLECTIBLE_FLOAT_HEIGHT;
  if (z <= ramp.zTo) return COLLECTIBLE_FLOAT_HEIGHT + (height * (z - ramp.zFrom)) / (ramp.zTo - ramp.zFrom);
  if (z <= platform.zTo) return COLLECTIBLE_FLOAT_HEIGHT + height;
  const t = (z - (platform.zTo - GOLDEN_RAMP.guideTakeOffBeforeEdge)) / GUIDE_SPEED;
  const apex = PHYSICS.jumpVelocity / PHYSICS.gravity;
  return COLLECTIBLE_FLOAT_HEIGHT + height + Math.max(0, jumpHeightAt(PHYSICS, t, apex));
}

const GOLDEN_POINT: CollectibleSpawnPoint = {
  id: GOLDEN_RAMP.bottle.spawnPointId,
  lane: GOLDEN_RAMP.bottle.lane,
  position: { x: laneCenterX(WORLD_CONFIG, GOLDEN_RAMP.bottle.lane), y: GOLDEN_RAMP.bottle.height, z: GOLDEN_RAMP.bottle.z },
};

export const SEG_GOLDEN_RAMP_60M: SegmentDefinition = {
  id: 'SEG_GOLDEN_RAMP_60M',
  kind: SegmentKind.Straight,
  displayName: 'Goldene Flasche – Rampe 60 m',
  length: GOLDEN_RAMP.length,
  width: WORLD_CONFIG.trackWidth,
  // The ramp overlay of SEG_08 (ENV_Ramp_A, modelled from the segment start) on three base road modules.
  visualAssetId: SEG_RAMP_40M.visualAssetId,
  extraVisuals: [0, 20, 40].map((z) => ({ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z } })),

  entry: entryConnector(),
  exits: [straightExit(GOLDEN_RAMP.length)],
  lanes: standardLanes(WORLD_CONFIG),

  // Same walkable geometry as SEG_RAMP_40M – the overlay GLB is modelled on it.
  terrain: SEG_RAMP_40M.terrain,

  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },

  collectibleSpawnPoints: [
    ...collectibleLine(WORLD_CONFIG, 'GUIDE', [
      { lane: RAMP.lane, from: 1, to: RAMP.platform.zTo - 1, spacing: SPACING, height: guideHeight },
      { lane: RAMP.lane, from: GOLDEN_RAMP.guideArc.from, to: GOLDEN_RAMP.guideArc.to, spacing: SPACING, height: guideHeight },
    ]),
    GOLDEN_POINT,
  ],
  fixedCollectibles: [{ spawnPointId: GOLDEN_POINT.id, collectibleId: GOLDEN_RAMP.bottle.collectibleId, scale: GOLDEN_RAMP.bottle.scale }],

  ...moduleSides(GOLDEN_RAMP.length),

  generation: {
    // unused: the schedule places the module (LevelGenerator) – it is never picked at random
    weight: 1,
    minDistance: FIRST_MARK,
    minSegmentsAfterJunction: 1,
    // never right after the plain ramp (and vice versa)
    family: SEG_RAMP_40M.generation.family,
    schedule: { stretches, window, forceAfter, sightLine, priority },
    tags: ['ramp', 'terrain', 'platform', 'setpiece', 'golden'],
  },
};
